/**
 * BeraterWizardWrapper — Sync-Hülle um die Berater-Wizard-Route
 * (`/case/:id/wizard/*`).
 *
 * Zweck: Die Wizard-Steps schreiben über `useDimensionForm` die Mandant-
 * (Erfassungs-)Ebene. Ohne gemounteten Sync blieben Berater-Eingaben rein
 * lokal (localStorage) — der Papier-Workflow („Berater erfasst alles selbst")
 * wäre nicht persistent. Dieser Wrapper startet `useWizardSync` mit
 * `layer='mandant'`, sodass die Erfassung in die DB geschrieben wird (RLS 0029
 * erlaubt Berater/Admin das Schreiben der Mandant-Ebene).
 *
 * KEIN WizardAudienceProvider: der Berater-Pfad ist der Default ('berater'),
 * Navigation/Footer bleiben auf Hub/Cockpit ausgerichtet. Der sichtbare
 * SaveStatusIndicator spiegelt den ECHTEN Sync-Status — kein falsches
 * „Gespeichert" mehr.
 */

import { type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  SaveStatusIndicator,
  type SaveStatusKind,
} from '../components/mandant/SaveStatusIndicator';
import { useWizardSync } from '../lib/store/useWizardSync';
import { useAuthOrSession } from '../lib/authOrSession';
import { useWizardStore } from '../lib/store/wizard';

export function BeraterWizardWrapper({ children }: { children: ReactNode }) {
  const { id: caseId = '' } = useParams<{ id?: string }>();
  const auth = useAuthOrSession();

  // Sync läuft nur mit echtem User. Im Mock bleibt status='idle'.
  const sync = useWizardSync(caseId, {
    userId: auth.isMock ? null : (auth.user?.id ?? null),
    // Berater-Erfassung (Papier-Workflow) schreibt in die gemeinsame Mandant-
    // Erfassungs-Ebene. Die fachliche Overlay-Ebene ('berater') bearbeitet der
    // Berater separat im Cockpit.
    layer: 'mandant',
  });

  const lastSavedAt = useWizardStore((s) => s.cases[caseId]?.lastSavedAt) ?? null;

  const indicatorKind: SaveStatusKind =
    sync.status === 'error'
      ? 'error'
      : sync.status === 'saving' || sync.status === 'loading'
        ? 'saving'
        : lastSavedAt
          ? 'saved'
          : 'idle';

  return (
    <div className="relative">
      <div className="sticky top-[var(--header-h)] z-[5] -mx-1 flex justify-end px-1 pt-3 md:pt-4">
        <SaveStatusIndicator
          status={indicatorKind}
          lastSavedAtIso={lastSavedAt}
          onRetry={() => {
            void sync.flush();
          }}
        />
      </div>
      {children}
    </div>
  );
}
